import styles from "./TotalSection.module.css";
import { ExpandableButton } from "../common/ExpandableButton";
import { Receipt } from "@/types/receipt";
import {
  translate,
  formatCurrency,
  formatPercentage,
} from "@/utils/formatters";

interface TotalItemProps {
  label: string;
  amount?: number;
  currencyCode: string;
}

const TotalItem = ({ label, amount, currencyCode }: TotalItemProps) => {
  if (amount == null) return null;
  return (
    <p className="amount small">
      {label}
      {formatCurrency(amount, currencyCode, true)}
    </p>
  );
};

interface TotalSectionProps {
  receipt: Receipt;
  lang: string;
  expandedTotal: boolean;
  setExpandedTotal: (e: boolean) => void;
}

export const TotalSection = ({
  receipt,
  lang,
  expandedTotal,
  setExpandedTotal,
}: TotalSectionProps) => {
  const { total } = receipt;



  const hasReturn = !!(
    total.extra_sum_values?.sum_return && total.extra_sum_values.sum_return > 0
  );
  const hasDiscount = !!(
    total.extra_sum_values?.sum_discount &&
    total.extra_sum_values.sum_discount > 0
  );
  const hasVat = !!total.sum_additions?.sum_vat_groups?.length;


  return (
    <div className="box head total">
      <div className="split">
        <div>
          <ExpandableButton
            onClick={() => setExpandedTotal(!expandedTotal)}
            isExpanded={expandedTotal}
            hasExpandableContent={!!(hasDiscount || hasReturn || hasVat)}
          >
            <>
              {translate(lang, "Totalsum", "Total sum")}
              {total.money_back && (
                <span className={`tag tag--returned ${styles.returnedTag}`}>
                  {translate(lang, "Returnert", "Returned")}
                </span>
              )}
            </>
          </ExpandableButton>
          {total.price_without_vat != null && (
            <p className={`${styles.breakdownItem} small amount`}>
              {translate(lang, "Pris eks MVA: ", "Price ex VAT: ")}
              {formatCurrency(
                total.price_without_vat,
                total.merchant_currency,
                true
              )}
            </p>
          )}
        </div>
        <div className={styles.summaryContainer}>
          <p className="amount">
            {formatCurrency(
              total.extra_sum_values?.final_price_rounded ?? total.final_price,
              total.merchant_currency,
              false,
              Boolean(total.money_back)
            )}
          </p>
          <div className={styles.breakdownWrapper}>
            {hasDiscount && (
              <p className={`${styles.breakdownItem} small amount`}>
                {translate(lang, "Total rabatt: ", "Total discount: ")}
                {formatCurrency(
                  total.extra_sum_values!.sum_discount!,
                  total.merchant_currency
                )}
              </p>
            )}
            {hasReturn && (
              <p className={`${styles.breakdownItem} small amount`}>
                {translate(lang, "Total retur: ", "Total return: ")}
                {formatCurrency(
                  total.extra_sum_values!.sum_return!,
                  total.merchant_currency
                )}
              </p>
            )}
            {hasVat && (
              <p className={`${styles.breakdownItem} small amount`}>
                {translate(lang, "MVA totalt: ", "Total VAT: ")}
                {formatCurrency(
                  total.sum_additions!.sum_vat_groups.reduce(
                    (a, g) => a + (g.amount ?? 0),
                    0
                  ),
                  total.merchant_currency
                )}
              </p>
            )}
          </div>
        </div>
      </div>
      {expandedTotal && (
        <>

          {total.sum_additions?.sum_vat_groups?.length && (
            <div className={styles.vatBreakdown}>
              <div className={styles.vatGridHeader}>
                <span className={`small ${styles.vatLabel}`}>
                  {translate(lang, "MVA %", "VAT %")}
                </span>
                <span className={`small ${styles.vatLabel}`}>
                  {translate(lang, "Grunnlag", "Base")}
                </span>
                <span className={`small ${styles.vatLabel}`}>
                  {translate(lang, "MVA", "VAT")}
                </span>
              </div>
              {total.sum_additions.sum_vat_groups.map((g, i) => (
                <div
                  key={`vat-${i}-${g.percentage}-${g.base_amount}`}
                  className={`amount small ${styles.vatGridRow}`}
                >
                  <span>{formatPercentage(g.percentage)}</span>
                  <span>
                    {g.base_amount === undefined
                      ? "-"
                      : formatCurrency(
                          g.base_amount,
                          total.merchant_currency,
                          true
                        )}
                  </span>
                  <span>
                    {g.amount === undefined
                      ? "-"
                      : formatCurrency(g.amount, total.merchant_currency, true)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <TotalItem
            label={translate(
              lang,
              "Opprinnelig pris inkl. MVA: ",
              "Original price inc VAT: "
            )}
            amount={total.sum_art_price_with_vat}
            currencyCode={total.merchant_currency}
          />
          {total.discounts && total.discounts.length > 0 && (
            <div className={styles.discountsSection}>
              {total.discounts.map((discount, index) => (
                <div
                  key={`discount-${discount.description}-${discount.amount}-${discount.percentage}-${index}`}
                  className={styles.discountItem}
                >
                  <span className={styles.discountLabel}>
                    {discount.description || translate(lang, "Rabatt", "Discount")}
                    {discount.percentage != null && `: ${formatPercentage(discount.percentage)}`}
                  </span>
                  <span className={styles.discountAmount}>
                    {discount.amount != null && formatCurrency(discount.amount, total.merchant_currency, true)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <TotalItem
            label={translate(lang, "Pris ink MVA: ", "Price inc VAT: ")}
            amount={total.price_with_vat}
            currencyCode={total.merchant_currency}
          />
          <TotalItem
            label={translate(lang, "Tips: ", "Tip: ")}
            amount={total.sum_additions?.sum_tip}
            currencyCode={total.merchant_currency}
          />
          <TotalItem
            label={translate(lang, "Pantavgift: ", "Recycling Fee: ")}
            amount={total.sum_additions?.sum_recycling}
            currencyCode={total.merchant_currency}
          />
          <TotalItem
            label={translate(
              lang,
              "Sluttsum før avrunding: ",
              "Total before rounding: "
            )}
            amount={total.final_price}
            currencyCode={total.merchant_currency}
          />
          {total.extra_sum_values?.final_price_rounded != null && (
            <TotalItem
              label={translate(lang, "Avrunding: ", "Rounding: ")}
              amount={
                total.extra_sum_values.final_price_rounded - total.final_price
              }
              currencyCode={total.merchant_currency}
            />
          )}
        </>
      )}
    </div>
  );
};
